/** "Today's labour risks" exception card for the Labour Management demo screen. Each card
 * surfaces a single actionable risk with a suggested action button that jumps the user to
 * the relevant tab (see `LabourRiskAction.target` in `types.ts`). */

import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LabourRisk } from '../types';
import { StatusChip } from './StatusChip';

interface RiskCardProps {
  risk: LabourRisk;
  onAction: (risk: LabourRisk) => void;
}

const SEVERITY_LABEL = { good: 'Low', warning: 'Warning', critical: 'Critical' };

const RiskCard = ({ risk, onAction }: RiskCardProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: theme.palette.custom.gray[300],
      borderRadius: '10px',
      p: 3,
    }}>
      <Stack direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}>
        <Box>
          <Typography variant="textSM"
            fontWeight={600}>
            {`${risk.site} \u00b7 ${risk.servicePeriod}`}
          </Typography>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mt: 0.5 }}>
            {risk.reason}
          </Typography>
        </Box>
        <StatusChip status={risk.severity}
          label={t(`labourManagementSeverity${SEVERITY_LABEL[risk.severity]}`, SEVERITY_LABEL[risk.severity])} />
      </Stack>
      <Box sx={{ mt: 2 }}>
        <Button variant="secondary"
          size="md"
          onClick={() => onAction(risk)}>
          {risk.action.label}
        </Button>
      </Box>
    </Box>
  );
};

export default RiskCard;
